import type { ApiResponse } from '@/types/api'
import type {
  BlogCategory,
  BlogCategoryCreateData,
  BlogCategoryUpdateData,
  BlogPost,
  BlogPostCreateData,
  BlogPostFilters,
  BlogPostUpdateData,
} from '@/types/blog'
import { apiDelete, apiGet, apiPost, apiPut } from './http'

// API du blog
export const blogApi = {
  /**
   * Récupérer la liste des posts avec filtres optionnels
   */
  async getPosts(filters?: BlogPostFilters): Promise<ApiResponse<BlogPost[]>> {
    console.log('[BlogAPI] getPosts:', filters)
    // Convertir les filtres en paramètres de requête, en excluant les valeurs undefined
    const params: Record<string, string | number> = {}
    if (filters) {
      if (filters.isDraft !== undefined) {
        params.isDraft = filters.isDraft ? 'true' : 'false'
      }
      if (filters.categoryId !== undefined) {
        params.categoryId = filters.categoryId
      }
      if (filters.authorId !== undefined) {
        params.authorId = filters.authorId
      }
      if (filters.limit !== undefined) {
        params.limit = filters.limit
      }
      if (filters.offset !== undefined) {
        params.offset = filters.offset
      }
    }
    return apiGet<ApiResponse<BlogPost[]>>('/blog-posts', params)
  },

  /**
   * Récupérer un post par son ID
   */
  async getPostById(id: number): Promise<BlogPost> {
    console.log('[BlogAPI] getPostById:', id)
    const response = await apiGet<ApiResponse<BlogPost>>(`/blog-posts/${id}`)
    console.log('[BlogAPI] getPostById response:', response)
    return response.data
  },

  /**
   * Récupérer un post par son slug
   */
  async getPostBySlug(slug: string): Promise<BlogPost> {
    console.log('[BlogAPI] getPostBySlug:', slug)
    const response = await apiGet<ApiResponse<BlogPost>>(`/blog-posts/slug/${slug}`)
    return response.data
  },

  /**
   * Créer un nouveau post
   */
  async createPost(data: BlogPostCreateData): Promise<BlogPost> {
    console.log('[BlogAPI] createPost:', data)
    const response = await apiPost<ApiResponse<BlogPost>>('/blog-posts', data)
    return response.data
  },

  /**
   * Mettre à jour un post
   */
  async updatePost(id: number, data: BlogPostUpdateData): Promise<BlogPost> {
    console.log('[BlogAPI] updatePost:', id, data)
    const response = await apiPut<ApiResponse<BlogPost>>(`/blog-posts/${id}`, data)
    return response.data
  },

  /**
   * Supprimer un post
   */
  async deletePost(id: number): Promise<void> {
    console.log('[BlogAPI] deletePost:', id)
    await apiDelete<ApiResponse<void>>(`/blog-posts/${id}`)
  },

  /**
   * Récupérer toutes les catégories
   */
  async getCategories(includePostCount: boolean = false): Promise<ApiResponse<BlogCategory[]>> {
    console.log('[BlogAPI] getCategories, includePostCount:', includePostCount)
    return apiGet<ApiResponse<BlogCategory[]>>('/blog-categories', {
      includePostCount: includePostCount ? 'true' : undefined,
    })
  },

  /**
   * Récupérer une catégorie par son ID
   */
  async getCategoryById(id: number): Promise<BlogCategory> {
    console.log('[BlogAPI] getCategoryById:', id)
    const response = await apiGet<ApiResponse<BlogCategory>>(`/blog-categories/${id}`)
    return response.data
  },

  /**
   * Créer une nouvelle catégorie
   */
  async createCategory(data: BlogCategoryCreateData): Promise<BlogCategory> {
    console.log('[BlogAPI] createCategory:', data)
    const response = await apiPost<ApiResponse<BlogCategory>>('/blog-categories', data)
    return response.data
  },

  /**
   * Mettre à jour une catégorie
   */
  async updateCategory(id: number, data: BlogCategoryUpdateData): Promise<BlogCategory> {
    console.log('[BlogAPI] updateCategory:', id, data)
    const response = await apiPut<ApiResponse<BlogCategory>>(`/blog-categories/${id}`, data)
    return response.data
  },

  /**
   * Supprimer une catégorie
   */
  async deleteCategory(id: number): Promise<void> {
    console.log('[BlogAPI] deleteCategory:', id)
    await apiDelete<ApiResponse<void>>(`/blog-categories/${id}`)
  },
}
